import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Lightbulb,
  Package,
  Wrench,
  Building2,
  BookOpen,
  CalendarDays,
  Rss,
  ImageIcon,
  ChevronLeft,
  ChevronRight,
  Target,
  Shield,
  VanIcon,
  Cable,
  Newspaper,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";

const navGroups = [
  {
    label: "Overview",
    items: [{ label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" }],
  },
  {
    label: "Content",
    items: [
      { label: "Pages", icon: FileText, path: "/pages" },
      { label: "Solutions", icon: Lightbulb, path: "/solutions" },
      { label: "Products", icon: Package, path: "/products" },
      { label: "Services", icon: Wrench, path: "/services" },
      { label: "Industries", icon: Building2, path: "/industries" },
    ],
  },
  {
    label: "Publishing",
    items: [
      { label: "Case Studies", icon: BookOpen, path: "/case-studies" },
      { label: "Webinars & Events", icon: CalendarDays, path: "/webinars" },
      { label: "Blog", icon: Rss, path: "/blog" },
      { label: "Vacancy", icon: VanIcon, path: "/vacancies" },
      { label: "Careers", icon: Cable, path: "/careers" },
      { label: "Media Library", icon: ImageIcon, path: "/media" },
    ],
  },
  {
    label: "Marketing",
    items: [
      { label: "Leads", icon: Target, path: "/leads" },
      { label: "Newsletter", icon: Newspaper, path: "/newsletter" },
    ],
  },
  {
    label: "Administration",
    items: [{ label: "Users & Roles", icon: Shield, path: "/staff" }],
  },
];

// ── Shared nav list ───────────────────────────────────────────────────────────
function NavList({ collapsed, onLinkClick }) {
  const { pathname } = useLocation();
  const isActive = (path) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path);

  return (
    <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
      {navGroups.map((group) => (
        <div key={group.label}>
          {!collapsed ? (
            <p className="sidebar-group-label mb-2">{group.label}</p>
          ) : (
            <div className="h-px bg-[hsl(var(--sidebar-border))] mx-1 mb-2" />
          )}
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={onLinkClick}
                    title={collapsed ? item.label : undefined}
                    className={cn(
                      "nav-item",
                      active && "active",
                      collapsed && "justify-center px-0 py-2.5",
                    )}
                  >
                    <item.icon
                      className={cn(
                        "nav-icon flex-shrink-0 transition-colors",
                        active
                          ? "text-[hsl(var(--sidebar-primary))]"
                          : "text-[hsl(var(--sidebar-muted))]",
                        collapsed ? "w-5 h-5" : "w-4 h-4",
                      )}
                    />
                    {!collapsed && (
                      <span className="truncate">{item.label}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

// ── Logo strip ────────────────────────────────────────────────────────────────
function LogoStrip({ collapsed, showClose, onClose }) {
  return (
    <div
      className={cn(
        "flex items-center border-b border-[hsl(var(--sidebar-border))] flex-shrink-0 transition-all duration-300",
        collapsed ? "h-14 px-3 justify-center" : "h-14 px-5 gap-3",
      )}
    >
      <div
        className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center"
        style={{ background: "var(--gradient-accent)" }}
      >
        <span className="text-white font-bold text-sm">T</span>
      </div>
      {!collapsed && (
        <div className="slide-in flex-1">
          <p className="text-white font-bold text-sm leading-none">
            ThynkProcess
          </p>
          <p
            className="text-[10px] mt-0.5"
            style={{ color: "hsl(var(--sidebar-muted))" }}
          >
            CMS Admin
          </p>
        </div>
      )}
      {showClose && !collapsed && (
        <button
          onClick={onClose}
          className="ml-auto p-1.5 rounded-lg text-[hsl(var(--sidebar-muted))] hover:bg-[hsl(var(--sidebar-accent))]"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  // Auto-close drawer on route change
  const { pathname } = useLocation();
  useEffect(() => {
    onMobileClose?.();
  }, [pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      {/* ── Desktop sidebar ─────────────────────────────────────────────── */}
      <aside
        className={cn(
          "hidden lg:flex flex-col h-screen border-r transition-all duration-300 ease-in-out flex-shrink-0",
          "bg-[hsl(var(--sidebar-background))] border-[hsl(var(--sidebar-border))]",
          collapsed ? "w-16" : "w-60",
        )}
      >
        <LogoStrip collapsed={collapsed} />
        <NavList collapsed={collapsed} />
        <div className="border-t border-[hsl(var(--sidebar-border))] p-2">
          <button
            onClick={onToggle}
            className={cn(
              "w-full flex items-center rounded-lg px-3 py-2.5 text-xs font-medium transition-all",
              "text-[hsl(var(--sidebar-muted))] hover:bg-[hsl(var(--sidebar-accent))] hover:text-[hsl(var(--sidebar-accent-foreground))]",
              collapsed && "justify-center px-0",
            )}
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-2" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* ── Mobile backdrop ──────────────────────────────────────────────── */}
      <div
        onClick={onMobileClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden",
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        )}
      />

      {/* ── Mobile drawer ────────────────────────────────────────────────── */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 flex flex-col h-screen w-72 transition-transform duration-300 ease-in-out lg:hidden",
          "bg-[hsl(var(--sidebar-background))] border-r border-[hsl(var(--sidebar-border))]",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <LogoStrip collapsed={false} showClose onClose={onMobileClose} />
        <NavList collapsed={false} onLinkClick={onMobileClose} />
      </aside>
    </>
  );
}
